import React, { useState, useEffect } from 'react';
import { NavLink, useLocation, useParams } from 'react-router-dom';
import { HomeOutlined, ScheduleOutlined, SisternodeOutlined, OneToOneOutlined, TrophyOutlined, TeamOutlined, SolutionOutlined, DesktopOutlined, WarningFilled, EyeOutlined, HeartOutlined, FieldTimeOutlined, OrderedListOutlined, CoffeeOutlined, ShopOutlined } from '@ant-design/icons';
import { _T } from "../../base-react/src/Localization";
import { Badge, Grid, Layout, Menu, Spin, Typography, theme } from 'antd';
import { useCompetition } from '../../base-react/hooks/Competition';
import { apis, hooks, models } from '@vote4dance/judging-sdk';
import { LogoMenuItem } from "../../base-react/components/BaseMenu";
import { CheckinNavMenuItem } from '../pages/competition/checkin/components/CheckinNavMenuItem';
import { useUserSettings } from '../../base-react/hooks/Storage';
var CompetitionValidationSource = models.competitions.CompetitionValidationSource;
var StreamHandler = apis.streaming.StreamHandler;
var SessionState = hooks.sessions.SessionState;
var useSession = hooks.sessions.useSession;
import { useRegistrationNavMenuItemState } from '../pages/competition/registration/components/RegistrationNavMenuItem';
import { useCafeNavMenuItemState } from '../pages/event/cafe/components/CafeNavMenuItem';
const { Text } = Typography;
const { useBreakpoint } = Grid;
export const Sider = ({ event_id, style }) => {
    const { settings, updateSettings } = useUserSettings();
    const screens = useBreakpoint();
    useEffect(() => {
        // Collapse the sider if on extra small screens
        if (screens.xs && !settings.managerLeftSiderCollapsed) {
            updateSettings({ managerLeftSiderCollapsed: true });
        }
    }, [screens]);
    return (React.createElement("div", { className: "manager-left-sider", style: { overflow: 'auto', height: '100vh', position: 'sticky', left: 0, top: 0, bottom: 0 } },
        React.createElement(Layout.Sider, { width: 280, collapsedWidth: 70, collapsible: true, collapsed: settings.managerLeftSiderCollapsed, onCollapse: (collapsed, type) => type === 'clickTrigger' && updateSettings({ managerLeftSiderCollapsed: collapsed }), style: style },
            React.createElement(LogoMenuItem, { collapsed: settings.managerLeftSiderCollapsed }),
            React.createElement(SiderMenu, { event_id: event_id }))));
};
export const SiderMenu = ({ event_id, onClick }) => {
    const [defaultCompetitionId, setDefaultCompetitionId] = useState();
    const [inputCompetition, event, competitions, loading] = useCompetition(event_id);
    const sessionState = useSession();
    const location = useLocation();
    let { competition_id } = useParams();
    useEffect(() => {
        if (competition_id) {
            setDefaultCompetitionId(competition_id);
        }
        else {
            if (competitions && competitions.length > 0) {
                setDefaultCompetitionId(competitions[0].competition_id);
            }
            else {
                setDefaultCompetitionId(inputCompetition === null || inputCompetition === void 0 ? void 0 : inputCompetition.competition_id);
            }
        }
    }, [competitions, inputCompetition]);
    const selectedRoute = () => {
        const parts = location.pathname.split('/');
        if (parts.length <= 3)
            return ['overview'];
        if (parts[3] === 'comp')
            return [parts[5]];
        return [parts[3]];
    };
    const validationMenu = useValidationMenu(competitions && competitions.length > 0 ? competitions : inputCompetition ? [inputCompetition] : []);
    const cafeMenuItem = useCafeNavMenuItemState({ event_id });
    const registrationMenuItem = useRegistrationNavMenuItemState({
        event,
        competitions,
        defaultCompetitionId
    });
    const { token } = theme.useToken();
    const highlightColor = token.colorPrimary;
    const isTester = SessionState.isTester(sessionState);
    const menuItems = [
        {
            key: "navigation",
            type: "group",
            children: [
                { key: "overview", icon: React.createElement(HomeOutlined, null), label: React.createElement(NavLink, { to: "" }, _T('sider.overview')) },
                validationMenu
            ].filter(Boolean)
        },
        {
            key: "setup",
            label: _T('sider.event'),
            type: "group",
            children: [
                { key: "competitions", icon: React.createElement(SisternodeOutlined, null), label: React.createElement(NavLink, { to: "competitions" }, _T('competition.children.title')) },
                { key: "floor", icon: React.createElement(OneToOneOutlined, null), label: React.createElement(NavLink, { to: "floor" }, _T('floor.title')) },
                { key: "users", icon: React.createElement(SolutionOutlined, null), label: React.createElement(NavLink, { to: "users" }, _T('users.title')) },
                { key: "cafe", icon: (React.createElement(Badge, { dot: cafeMenuItem.activeOrders > 0, color: highlightColor, offset: [0, 5] },
                        React.createElement(CoffeeOutlined, null))), label: cafeMenuItem.label },
                { key: "schedule", icon: React.createElement(ScheduleOutlined, null), label: React.createElement(NavLink, { to: "schedule" }, _T('schedule.title')) }
            ].filter(Boolean)
        },
        {
            key: "competition",
            label: _T('sider.competition'),
            type: "group",
            children: loading ? [
                { key: "loading", label: React.createElement(Spin, { key: "spin" }) }
            ] : [
                { key: "registration", icon: (React.createElement(Badge, { dot: registrationMenuItem.count > 0, color: highlightColor, offset: [0, 5] },
                        React.createElement(OrderedListOutlined, null))), label: registrationMenuItem.label },
                { key: "classes", icon: React.createElement(TrophyOutlined, null), label: React.createElement(NavLink, { to: `comp/${defaultCompetitionId}/classes` }, _T('class.plural')) },
                { key: "teams", icon: React.createElement(TeamOutlined, null), label: React.createElement(NavLink, { to: `comp/${defaultCompetitionId}/teams` }, _T('team.title')) },
                { key: "judges", icon: React.createElement(SolutionOutlined, null), label: React.createElement(NavLink, { to: `comp/${defaultCompetitionId}/judges` }, _T('judges.plural')) },
                { key: "checkin", icon: React.createElement(FieldTimeOutlined, null), label: React.createElement(CheckinNavMenuItem, { event: event, competitions: competitions, defaultCompetitionId: defaultCompetitionId }) }
            ].filter(Boolean)
        },
        {
            key: "public",
            label: _T('sider.audience'),
            type: "group",
            children: [
                { key: "notifications", icon: React.createElement(TeamOutlined, null), label: React.createElement(NavLink, { to: "notifications" }, _T('notifications.title')) },
                { key: "screens", icon: React.createElement(DesktopOutlined, null), label: React.createElement(NavLink, { to: "screens" }, _T('screen.plural')) },
                { key: "sponsors", icon: React.createElement(ShopOutlined, null), label: React.createElement(NavLink, { to: "sponsors" }, _T('sponsors.title')) },
                { key: "voting", icon: React.createElement(HeartOutlined, null), label: React.createElement(NavLink, { to: "voting" }, _T('voting.title')) },
                { key: "live", icon: React.createElement(EyeOutlined, null), label: React.createElement(NavLink, { to: `comp/${defaultCompetitionId}/live` }, _T('live.title')) }
            ]
                .filter(Boolean)
        }
    ];
    return (React.createElement(Menu, { selectedKeys: selectedRoute(), mode: "inline", items: menuItems, onClick: onClick }));
};
function useValidationMenu(competitions) {
    const [totalList, setTotalList] = useState({});
    useEffect(() => {
        if (!competitions || competitions.length === 0)
            return;
        const unsubscribeList = competitions.map(competition => StreamHandler.get().subscribe(CompetitionValidationSource(competition.competition_id).url, (data) => {
            const numInvalidReports = data ? data.filter((validation) => !validation.result).length : 0;
            setTotalList(prev => ({
                ...prev,
                [competition.competition_id]: numInvalidReports
            }));
        }));
        return () => {
            unsubscribeList.forEach(unsubscribe => {
                if (typeof unsubscribe === 'function')
                    unsubscribe();
            });
        };
    }, [competitions.map(c => c.competition_id).join(',')]);
    const totalValidations = Object.values(totalList).reduce((sum, count) => sum + (count || 0), 0);
    if (totalValidations === 0)
        return null;
    return {
        key: "validator",
        icon: React.createElement(WarningFilled, null),
        danger: true,
        label: (React.createElement(NavLink, { to: "validator" },
            React.createElement(Badge, { count: totalValidations, offset: [15, 10] },
                React.createElement(Text, { strong: true }, _T("validator.title")))))
    };
}
